import { FEATURE_KEYS, FEATURES, type PlatformPlan, USAGE_ITEM_LABELS } from "@station/contracts";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Tags } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlanDialog, PlanPriceDialog, UsagePricesDialog } from "../../features/platform/billing-dialogs.js";
import { formatDecimalText } from "../../features/platform/platform-values.js";
import { usePlatformPermissions, usePlatformPlans } from "../../features/platform/queries.js";
import { Field, PlatformPage } from "../../features/platform/shared.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount, formatDate } from "../../lib/format.js";

export const Route = createFileRoute("/platform/planlar")({ component: PlansRoute });

type Editing =
  | { kind: "plan"; plan: PlatformPlan | null }
  | { kind: "price"; plan: PlatformPlan }
  | { kind: "usage"; plan: PlatformPlan };

/**
 * Planlar (Prompt 24 web): istasyon başına aylık fiyat (geçerlilik tarihli, geçmiş silinmez), yıllık indirim, deneme
 * süresi, azami istasyon, özellikler ve kullanım fiyatları (dahil miktar + birim fiyat).
 */
function PlansRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.plans.manage");
  const plans = usePlatformPlans();
  const [editing, setEditing] = useState<Editing | null>(null);
  const close = (open: boolean) => !open && setEditing(null);

  return (
    <PlatformPage
      title={t("platform.plans.title")}
      description={t("platform.plans.description")}
      permission="platform.catalog.read"
      actions={
        canManage ? (
          <Button onClick={() => setEditing({ kind: "plan", plan: null })}>
            <Plus aria-hidden="true" />
            {t("platform.plans.new")}
          </Button>
        ) : null
      }
    >
      {plans.error ? (
        <Alert tone="danger">{errorMessage(plans.error)}</Alert>
      ) : plans.isPending ? (
        <SkeletonRows rows={6} />
      ) : plans.data.length === 0 ? (
        <EmptyState headingLevel={2} icon={<Tags />} title={t("platform.plans.empty")} />
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-2">
          {plans.data.map((plan) => (
            <Card key={plan.code} className="grid content-start gap-4 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold">
                    {plan.name} <span className="font-mono text-sm text-muted-foreground">{plan.code}</span>
                  </h2>
                  {plan.description ? (
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {plan.isActive ? (
                    <StatusBadge tone="success">{t("platform.plans.active")}</StatusBadge>
                  ) : (
                    <StatusBadge>{t("platform.inactive")}</StatusBadge>
                  )}
                  <StatusBadge tone="info">
                    {t("platform.plans.tenantCount", { count: plan.tenantCount })}
                  </StatusBadge>
                </div>
              </div>
              <dl className="grid gap-4 sm:grid-cols-4">
                <Field label={t("platform.fields.stationMonthlyPrice")}>
                  {plan.currentPrice ? <MoneyText value={plan.currentPrice.stationMonthlyPrice} /> : "—"}
                </Field>
                <Field label={t("platform.fields.yearlyDiscount")}>
                  {plan.currentPrice ? `%${formatDecimalText(plan.currentPrice.yearlyDiscountPercent)}` : "—"}
                </Field>
                <Field label={t("platform.fields.trialDays")}>
                  {t("platform.subscription.days", { count: plan.trialDays })}
                </Field>
                <Field label={t("platform.fields.maxStations")}>
                  {plan.maxStations === null ? t("platform.unlimited") : formatCount(plan.maxStations)}
                </Field>
              </dl>
              <div className="grid gap-1">
                <h3 className="text-sm font-semibold">{t("platform.plans.features")}</h3>
                <ul className="flex flex-wrap gap-1">
                  {[...plan.features]
                    .sort((a, b) => FEATURE_KEYS.indexOf(a) - FEATURE_KEYS.indexOf(b))
                    .map((feature) => (
                      <li key={feature}>
                        <StatusBadge>{FEATURES[feature]}</StatusBadge>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="grid gap-1">
                <h3 className="text-sm font-semibold">{t("platform.plans.usagePrices")}</h3>
                {plan.usagePrices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("platform.plans.noUsagePrices")}</p>
                ) : (
                  <ul className="grid gap-1 text-sm">
                    {plan.usagePrices.map((price) => (
                      <li key={price.itemCode} className="flex flex-wrap justify-between gap-2">
                        <span>{USAGE_ITEM_LABELS[price.itemCode]}</span>
                        <span className="text-muted-foreground">
                          {t("platform.plans.included", { count: price.includedQuantity })} ·{" "}
                          <MoneyText value={price.unitPrice} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer font-medium">
                  {t("platform.plans.priceHistory", { count: plan.prices.length })}
                </summary>
                <ul className="mt-2 grid gap-1">
                  {plan.prices.map((price) => (
                    <li key={price.id} className="flex flex-wrap justify-between gap-2">
                      <span className="tabular">{formatDate(price.validFrom)}</span>
                      <span>
                        <MoneyText value={price.stationMonthlyPrice} /> · %
                        {formatDecimalText(price.yearlyDiscountPercent)}
                        {price.note ? <span className="text-muted-foreground"> · {price.note}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
              {canManage ? (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "plan", plan })}>
                    {t("platform.plans.edit")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "price", plan })}>
                    {t("platform.plans.newPrice")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ kind: "usage", plan })}>
                    {t("platform.plans.editUsage")}
                  </Button>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
      {editing?.kind === "plan" ? <PlanDialog open onOpenChange={close} plan={editing.plan} /> : null}
      {editing?.kind === "price" ? <PlanPriceDialog open onOpenChange={close} plan={editing.plan} /> : null}
      {editing?.kind === "usage" ? <UsagePricesDialog open onOpenChange={close} plan={editing.plan} /> : null}
    </PlatformPage>
  );
}
